import React, { useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * CaptchaGate
 *
 * Renders a Cloudflare Turnstile widget inline. Exposes a single imperative
 * method via ref: `verify(action)` returns a Promise that resolves to true
 * if the widget solved AND our backend confirmed the token with Cloudflare,
 * or false otherwise.
 *
 * Used by AuthModal before every sign-in / sign-up / forgot-password call.
 *
 * Widget is `appearance="interaction-only"` so honest users see nothing in
 * the form (most challenges are invisible / passive). Only suspicious
 * sessions get a visible challenge.
 */
const CaptchaGate = React.forwardRef(({ action }, ref) => {
  const widgetRef = useRef(null);
  const tokenRef = useRef(null);
  const [status, setStatus] = React.useState('idle'); // 'idle' | 'ready' | 'errored'

  React.useImperativeHandle(ref, () => ({
    /** Returns true if captcha solved + backend-verified, else false.
     *  If the widget never loaded (hostname not whitelisted in Cloudflare,
     *  or no SITE_KEY configured) we pass through - the backend still
     *  validates any submitted token, so a missing widget can't be abused
     *  from production where the widget DOES load. */
    async verify(currentAction = action) {
      if (!SITE_KEY) return true;
      if (status === 'errored') return true; // widget couldn't load -> skip
      const token = tokenRef.current;
      if (!token) {
        // Widget loaded but user hasn't solved yet - block.
        if (status === 'ready') return false;
        // Widget never loaded - treat as no-op.
        return true;
      }
      try {
        const r = await fetch(`${API_BASE}/api/auth/turnstile/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, action: currentAction }),
        });
        const ok = r.ok;
        try { widgetRef.current?.reset(); } catch { /* ignore */ }
        tokenRef.current = null;
        return ok;
      } catch {
        return false;
      }
    },
    reset() {
      try { widgetRef.current?.reset(); } catch { /* ignore */ }
      tokenRef.current = null;
    },
  }));

  if (!SITE_KEY || status === 'errored') return null;

  return (
    <div className="mt-3 flex justify-center" data-testid="captcha-gate">
      <Turnstile
        ref={widgetRef}
        siteKey={SITE_KEY}
        options={{
          appearance: 'interaction-only',
          theme: 'light',
          size: 'normal',
        }}
        onSuccess={(t) => { tokenRef.current = t; setStatus('ready'); }}
        onExpire={() => { tokenRef.current = null; }}
        onError={() => { tokenRef.current = null; setStatus('errored'); }}
      />
    </div>
  );
});

CaptchaGate.displayName = 'CaptchaGate';
export default CaptchaGate;
